import { useState } from "react";
import XenieLogoDark from "../../Assets/XenieLogoDark.png";
import eyeOff from "../../Assets/eyeIconOff.svg";
import eye from "../../Assets/eye.svg";
import CircularProgress from "../../Components/LoadingModal/LoadingModal";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import TickMark from '../../Assets/Modals/TickMark.svg';
import useAuthentication from "../../Hooks/useAuthentication";

const SignIn = () => {
    const { setAuthenticated } = useAuthentication();

    const [password, setPassword] = useState("");
    const [type, setType] = useState("password");
    const [icon, setIcon] = useState(eyeOff);
    const [formMessage, setFormMessage] = useState({
        email: "",
        password: "",
        submit: "",
    });
    const [isEmailFocused, setIsEmailFocused] = useState(false);
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [rememberMe, setRememberMe] = useState(false);

    const handleToggleRemember = (e) => {
        e.preventDefault();
        setRememberMe(!rememberMe);
    }

    const handleToggle = () => {
        if (type === "password") {
            setIcon(eye);
            setType("text");
        } else {
            setIcon(eyeOff);
            setType("password");
        }
    };

    const navigate = useNavigate();

    async function handleSignIn() {
        setIsLoading(true);
        const formEle = document.querySelector("form");
        const formData = new FormData(formEle);

        try {
            const data = {
                email: formData.get("email"),
                password: formData.get("password"),
            };
            const response = await axios.post(`${process.env.REACT_APP_API_URL}v1/sellerlogin`, data);

            if (response.status === 200) {
                setAuthenticated(response.data.data.accessToken, response.data.data.refreshToken);
                const roles = response.data.data.role;
                roles.sort();
                if (roles.includes("Admin")) {
                    navigate("/");
                    window.location.reload();
                } else if (roles.includes("Inventory Manager")) {
                    navigate("/inventory-management");
                    window.location.reload();
                } else if (roles.includes("Lens Manager")) {
                    navigate("/lens-and-reviews");
                    window.location.reload();
                } else if (roles.includes("Store Manager")) {
                    navigate("/storefront");
                    window.location.reload();
                } else {
                    navigate("/");
                    window.location.reload();
                }
            } else {
                let errors = {};
                errors.submit = "Please check your username or password";
                setFormMessage(errors);
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                setFormMessage({
                    submit: "Authentication failure. Please check your username or password.",
                });
            }
            else {
                setFormMessage({
                    submit: error?.response?.data?.message || "An error occurred. Please try again later.",
                });
            }
        } finally {
            setIsLoading(false);
        }
    }

    const handleNext = () => {
        let errors = {};
        const email = document.getElementById("email").value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            errors.email = "Enter your Email";
            setIsEmailFocused(true);
        } else if (!emailRegex.test(email)) {
            errors.email = "Please enter a valid Email";
            setIsEmailFocused(true);
        } else {
            setIsEmailFocused(false);
        }

        const password = document.getElementById("password").value;
        if (!password) {
            errors.password = "Enter your Password";
            setIsPasswordFocused(true);
        } else {
            setIsPasswordFocused(false);
        }

        if (Object.keys(errors).length === 0) {
            handleSignIn();
        } else {
            setFormMessage(errors);
        }
    };

    return (
        <>
            <div className="size-screen flex flex-col overflow-y-scroll">
                <div className="pt-28"></div>

                <div className="mx-auto w-[44%] p-10 flex flex-col gap-20 border border-[#EAEAEA] rounded-2xl">
                    <div className="h-11 w-auto">
                        <img className='h-full w-auto mx-auto' src={XenieLogoDark} alt="Xenie Logo" />
                    </div>

                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col">
                                <h1 className='font-semibold text-[32px] leading-[44px] text-[#121212]'>Welcome Back!</h1>
                                <p className='font-medium text-base text-[#666666]'>Log in to your dedicated Xenie Seller account</p>
                            </div>

                            <form action="submit" className="flex flex-col gap-8">
                                <div className="flex flex-col gap-3">
                                    {/* Email Input */}
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center justify-between">
                                            <label className='select-none cursor-pointer w-fit font-medium text-base text-[#121212]' htmlFor='email'>
                                                Email Address<span className="text-[#DC2626]">*</span>
                                            </label>
                                            {formMessage.email && (
                                                <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formMessage.email}</p>
                                            )}
                                        </div>
                                        <div className={`bg-[#F5F5F5] px-3 py-4 rounded-[9px] border ${isEmailFocused ? 'border-primaryColor' : 'border-[#EAEAEA]'} focus-within:border-primaryColor`}>
                                            <input 
                                                className='w-full outline-none bg-transparent' 
                                                type="email"
                                                name="email"
                                                id="email"
                                                placeholder="enter registered email address"
                                                autoComplete="email"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleNext();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Password Input */}
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center justify-between">
                                            <label className='select-none cursor-pointer w-fit font-medium text-base text-[#121212]' htmlFor='password'>
                                                Password<span className="text-[#DC2626]">*</span>
                                            </label>
                                            {formMessage.password && (
                                                <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formMessage.password}</p>
                                            )}
                                        </div>
                                        <div className={`bg-[#F5F5F5] px-3 py-4 rounded-[9px] border ${isPasswordFocused ? 'border-primaryColor' : 'border-[#EAEAEA]'} flex items-center gap-[10px] focus-within:border-primaryColor`}>
                                            <input 
                                                className='w-[calc(100%-24px)] outline-none bg-transparent' 
                                                type={type}
                                                name="password"
                                                id="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                autoComplete="current-password"
                                                placeholder="enter password"
                                                required
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleNext();
                                                    }
                                                }}
                                            />
                                            <button type="button" onClick={handleToggle} className="outline-none size-6 flex items-center justify-center">
                                                <img src={icon} alt="" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Remember Me and Forgot Password */}
                                <div className="flex items-center justify-between -mt-4">
                                    {/* <div className="flex items-center gap-2">
                                        <button type="button" onClick={handleToggleRemember} className={`outline-none w-6 h-6 ${rememberMe ? 'border-none bg-primaryColor' : 'border border-[#D1D5DB]'} rounded-md flex items-center justify-center`}>
                                            <img src={rememberMe ? TickMark : ''} alt="" />
                                        </button>
                                        <p className="font-normal text-[14px] leading-6 text-[#000000]">Remember me</p>
                                    </div> */}
                                    <Link to='/forgot-password' className="text-[14px] leading-6 ml-auto font-medium text-[#EF4444]">Forgot Password?</Link>
                                </div>

                                {/* Submit Button */}
                                <button 
                                    type="button"
                                    onClick={handleNext} 
                                    className='rounded-[9px] bg-primaryColor hover:bg-primaryAccent text-[#FFFFFF] px-3 py-4 font-semibold text-[18px] leading-6'
                                >
                                    Sign In
                                </button>

                                {/* Submit Error Message */}
                                {formMessage.submit && (
                                    <p className='text-[14px] leading-5 text-[#DC2626] select-none text-center'>{formMessage.submit}</p>
                                )}
                            </form>
                        </div>

                        <p className='font-medium text-[14px] leading-6 text-[#121212] mx-auto'>
                            Want to be a part of Xenie? <Link to='/apply-seller' className='text-primaryColor font-bold'>Fill the request form</Link>
                        </p>
                    </div>
                </div>
            </div>
            {isLoading && (
                <div className="loadingContainer">
                    <CircularProgress />
                </div>
            )}
        </>
    );
};

export default SignIn;
